import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getWorkOrderDetail, addWorkOrder, updateWorkOrder } from "../../api/workorders";
import { getClientList } from "../../api/client";
import { getProviderList } from "../../api/providers";
import { getStatusCatalogue } from "../../api/common/catalogues.ts";

export const WORK_ORDER_STATUS_CATALOGUE = "WORK_ORDER";

type Option = { id: string | number; [key: string]: any };

interface WorkOrderFormData {
  productionDate: Dayjs | null;
  originalCode: string;
  client: Option | null;
  name: string;
  description: string;
  quantity: number | string;
  material: string;
  sequence: string;
  requestedFrom: string;
  provider: Option | null;
  status: Option | null;
}

interface WorkOrderFormProps {
  id?: number | string;
  companyId?: number | string;
  onSubmit?: () => void;
}

const emptyValues: WorkOrderFormData = {
  productionDate: null,
  originalCode: "",
  client: null,
  name: "",
  description: "",
  quantity: 0,
  material: "",
  sequence: "",
  requestedFrom: "",
  provider: null,
  status: null,
};

const buildValidationSchema = (isEdit: boolean) =>
  Yup.object({
    productionDate: Yup.mixed().nullable(),
    client: Yup.object().nullable().required("El cliente es requerido"),
    name: Yup.string().trim().required("El nombre del trabajo es requerido"),
    quantity: Yup.number()
      .typeError("La cantidad debe ser numérica")
      .integer("La cantidad debe ser un número entero")
      .min(0, "La cantidad no puede ser negativa")
      .required("La cantidad es requerida"),
    provider: Yup.object().nullable(),
    status: isEdit
      ? Yup.object().nullable().required("El estatus es requerido")
      : Yup.object().nullable(),
  });

const fetchWorkOrderStatuses = () => getStatusCatalogue(WORK_ORDER_STATUS_CATALOGUE);

const emptyToNull = (value: string) => (value?.trim() ? value.trim() : null);

export default function WorkOrderForm({ id, companyId, onSubmit }: WorkOrderFormProps) {
  const isEdit = Boolean(id);
  const { openSnack } = useSnack();
  const [initialValues, setInitialValues] = useState<WorkOrderFormData>(emptyValues);
  const [loading, setLoading] = useState<boolean>(isEdit);

  useEffect(() => {
    if (!id) {
      setInitialValues(emptyValues);
      setLoading(false);
      return;
    }

    const fetchWorkOrder = async () => {
      setLoading(true);
      try {
        const { data } = await getWorkOrderDetail(id);
        setInitialValues({
          productionDate: data.productionDate ? dayjs(data.productionDate) : null,
          originalCode: data.originalCode ?? "",
          client: data.clientId
            ? { id: data.clientId, code: data.clientCode ?? "", name: data.clientName ?? "" }
            : null,
          name: data.name ?? "",
          description: data.description ?? "",
          quantity: data.quantity ?? 0,
          material: data.material ?? "",
          sequence: data.sequence ?? "",
          requestedFrom: data.requestedFrom ?? "",
          provider: data.providerId
            ? {
                id: data.providerId,
                code: data.providerCode ?? "",
                legalName: data.providerLegalName ?? "",
              }
            : null,
          status: data.statusId
            ? { id: data.statusId, code: data.statusCode ?? "", name: data.statusName ?? "" }
            : null,
        });
      } catch (error) {
        openSnack(handleError(error), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrder();
  }, [id]);

  const handleSubmit = async (values: WorkOrderFormData, { setSubmitting }: any) => {
    const payload = {
      productionDate: values.productionDate
        ? dayjs(values.productionDate).format("YYYY-MM-DD")
        : null,
      originalCode: emptyToNull(values.originalCode),
      clientId: values.client?.id ?? null,
      name: values.name.trim(),
      description: emptyToNull(values.description),
      quantity: Number(values.quantity) || 0,
      material: emptyToNull(values.material),
      sequence: emptyToNull(values.sequence),
      requestedFrom: emptyToNull(values.requestedFrom),
      providerId: values.provider?.id ?? null,
      statusId: values.status?.id ?? null,
    };

    try {
      if (id) {
        await updateWorkOrder(id, payload);
        openSnack("Orden de trabajo actualizada exitosamente", "success");
      } else {
        await addWorkOrder(payload);
        openSnack("Orden de trabajo creada exitosamente", "success");
      }
      onSubmit?.();
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={buildValidationSchema(isEdit)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => {
        const fieldError = (name: keyof WorkOrderFormData) =>
          Boolean(touched[name] && errors[name]);
        const fieldHelper = (name: keyof WorkOrderFormData) =>
          touched[name] && errors[name] ? String(errors[name]) : undefined;

        return (
          <Form>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MyDatePicker
                    label="Fecha de producción"
                    value={values.productionDate}
                    onChange={(date: Dayjs | null) => {
                      setFieldValue("productionDate", date);
                      setFieldTouched("productionDate", true, false);
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="originalCode"
                    label="Clave Sistema Malena"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <LGAutocomplete
                    fetchFunction={getClientList}
                    fetchBy="name"
                    queryParams={{ companyId: companyId ?? null }}
                    optionLabel={["code", "name"]}
                    optionSeparator=" | "
                    label="Cliente"
                    value={values.client}
                    onChange={(_event, value) => {
                      setFieldValue("client", value);
                      setFieldTouched("client", true, false);
                    }}
                    error={fieldError("client")}
                    helperText={fieldHelper("client")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LGAutocomplete
                    fetchFunction={getProviderList}
                    fetchBy="code"
                    queryParams={{ companyId: companyId ?? null }}
                    optionLabel={["code", "legalName"]}
                    optionSeparator=" | "
                    label="Proveedor"
                    value={values.provider}
                    onChange={(_event, value) => setFieldValue("provider", value)}
                    error={fieldError("provider")}
                    helperText={fieldHelper("provider")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="name"
                    label="Nombre del trabajo"
                    fullWidth
                    required
                    error={fieldError("name")}
                    helperText={fieldHelper("name")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="description"
                    label="Descripción"
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    name="quantity"
                    label="Cantidad"
                    type="number"
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 1 }}
                    error={fieldError("quantity")}
                    helperText={fieldHelper("quantity")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field as={TextField} name="material" label="Material" fullWidth />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field as={TextField} name="sequence" label="Secuencia" fullWidth />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    name="requestedFrom"
                    label="Solicitado por"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LGAutocomplete
                    fetchFunction={fetchWorkOrderStatuses}
                    fetchBy={null}
                    optionLabel="name"
                    label="Estatus"
                    value={values.status}
                    onChange={(_event, value) => {
                      setFieldValue("status", value);
                      setFieldTouched("status", true, false);
                    }}
                    error={fieldError("status")}
                    helperText={
                      fieldHelper("status") ??
                      (!isEdit ? "Si se deja vacío se asigna \"Pendiente\"" : undefined)
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ mt: 2, mb: 2 }}
                  >
                    {isSubmitting ? "Procesando..." : id ? "Actualizar" : "Crear"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
